'use client';

/**
 * Pagination - Core Component (Enhanced Wrapper)
 *
 * This component wraps shadcn/ui Pagination with complete pagination logic,
 * automatic page calculation, and ellipsis handling.
 *
 * Enhancements over shadcn/ui:
 * - Automatic visible page calculation based on currentPage and totalPages
 * - Smart ellipsis placement for large page counts
 * - Configurable maxVisiblePages (default: 5)
 * - Support for both onClick and href-based navigation
 * - Customizable previous/next labels via useLabel
 * - Proper disabled states for first/last page
 * - Accessibility: aria-current, aria-label, aria-disabled
 *
 * Props:
 * - totalPages, currentPage: Required pagination state
 * - onPageChange: Callback for page changes (SPA navigation)
 * - href: Base URL for page links (SSR/SEO navigation)
 * - maxVisiblePages: Max page numbers shown (default: 5)
 *
 * @see {@link file://./../ui/pagination.tsx} for the underlying implementation
 */
import { forwardRef } from 'react';
import {
  Pagination as ShadcnPagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/_components/ui/pagination';
import { useLabel } from '@/app/_contexts/labels-context';
import { cn } from '@/app/_lib/utils';

export interface PaginationProps {
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Current active page (1-indexed)
   */
  currentPage: number;
  /**
   * Callback when page changes
   */
  onPageChange?: (page: number) => void;
  /**
   * Maximum number of visible page numbers
   * @default 5
   */
  maxVisiblePages?: number;
  /**
   * Custom label for previous button
   * @default from useLabel('paginationPrevious')
   */
  previousLabel?: string;
  /**
   * Custom label for next button
   * @default from useLabel('paginationNext')
   */
  nextLabel?: string;
  /**
   * Base href for page links (page number will be appended)
   * If not provided, onClick will be used instead
   */
  href?: string;
  /**
   * Additional className for the pagination container
   */
  className?: string;
}

/**
 * Calculate which page numbers should be visible
 */
function getVisiblePages(
  currentPage: number,
  totalPages: number,
  maxVisible: number
): (number | 'ellipsis')[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const halfVisible = Math.floor(maxVisible / 2);

  // Always show first page
  pages.push(1);

  let startPage = Math.max(2, currentPage - halfVisible);
  let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

  // Adjust if we're near the start
  if (currentPage <= halfVisible + 1) {
    endPage = Math.min(totalPages - 1, maxVisible - 1);
  }

  // Adjust if we're near the end
  if (currentPage >= totalPages - halfVisible) {
    startPage = Math.max(2, totalPages - maxVisible + 2);
  }

  // Add ellipsis after first page if needed
  if (startPage > 2) {
    pages.push('ellipsis');
  }

  // Add middle pages
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Add ellipsis before last page if needed
  if (endPage < totalPages - 1) {
    pages.push('ellipsis');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

export const Pagination = forwardRef<HTMLElement, PaginationProps>(
  (
    {
      totalPages,
      currentPage,
      onPageChange,
      maxVisiblePages = 5,
      previousLabel,
      nextLabel,
      href,
      className,
    },
    ref
  ) => {
    const defaultPreviousLabel = useLabel('paginationPrevious', 'Previous');
    const defaultNextLabel = useLabel('paginationNext', 'Next');
    const goToPageLabel = useLabel('goToPage', 'Go to page');
    const paginationAriaLabel = useLabel('paginationAriaLabel', 'Pagination');
    const goToPreviousPageAriaLabel = useLabel('goToPreviousPageAriaLabel', 'Go to previous page');
    const goToNextPageAriaLabel = useLabel('goToNextPageAriaLabel', 'Go to next page');

    const effectivePreviousLabel = previousLabel ?? defaultPreviousLabel;
    const effectiveNextLabel = nextLabel ?? defaultNextLabel;

    const visiblePages = getVisiblePages(
      currentPage,
      totalPages,
      maxVisiblePages
    );

    const handlePageClick = (page: number) => {
      if (page !== currentPage && page >= 1 && page <= totalPages) {
        onPageChange?.(page);
      }
    };

    const isPreviousDisabled = currentPage <= 1;
    const isNextDisabled = currentPage >= totalPages;

    return (
      <ShadcnPagination ref={ref} className={className} aria-label={paginationAriaLabel}>
        <PaginationContent>
          {/* Previous Button */}
          <PaginationItem>
            <PaginationPrevious
              href={
                !isPreviousDisabled && href
                  ? `${href}${currentPage - 1}`
                  : undefined
              }
              onClick={
                !href && !isPreviousDisabled
                  ? (e) => {
                      e.preventDefault();
                      handlePageClick(currentPage - 1);
                    }
                  : undefined
              }
              aria-label={goToPreviousPageAriaLabel}
              className={cn(
                isPreviousDisabled &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
            >
              <span>{effectivePreviousLabel}</span>
            </PaginationPrevious>
          </PaginationItem>

          {/* Page Numbers */}
          {visiblePages.map((page, index) => {
            if (page === 'ellipsis') {
              return (
                <PaginationItem key={`ellipsis-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              );
            }

            const isActive = page === currentPage;

            return (
              <PaginationItem key={page}>
                <PaginationLink
                  href={href ? `${href}${page}` : undefined}
                  onClick={
                    !href
                      ? (e) => {
                          e.preventDefault();
                          handlePageClick(page);
                        }
                      : undefined
                  }
                  isActive={isActive}
                  aria-label={`${goToPageLabel} ${page}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          {/* Next Button */}
          <PaginationItem>
            <PaginationNext
              href={
                !isNextDisabled && href
                  ? `${href}${currentPage + 1}`
                  : undefined
              }
              onClick={
                !href && !isNextDisabled
                  ? (e) => {
                      e.preventDefault();
                      handlePageClick(currentPage + 1);
                    }
                  : undefined
              }
              aria-label={goToNextPageAriaLabel}
              className={cn(
                isNextDisabled &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
            >
              <span>{effectiveNextLabel}</span>
            </PaginationNext>
          </PaginationItem>
        </PaginationContent>
      </ShadcnPagination>
    );
  }
);

Pagination.displayName = 'Pagination';

export default Pagination;
